import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Star, Send, RefreshCcw, Ban, MessageSquare, Loader2, Inbox, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReseauLayout from '../../components/reseau/ReseauLayout';
import ReviewSuggestionsCard from '../../components/reseau/ReviewSuggestionsCard';
import useCockpitAction from '../../lib/useCockpitAction';

const STATUS_BADGE = {
    created: { label: 'Créée', color: 'bg-slate-100 text-slate-700' },
    sent: { label: 'Envoyée', color: 'bg-sky-100 text-sky-800' },
    opened: { label: 'Ouverte', color: 'bg-blue-100 text-blue-800' },
    started: { label: 'Démarrée', color: 'bg-indigo-100 text-indigo-800' },
    submitted: { label: 'Soumise', color: 'bg-emerald-100 text-emerald-800' },
    expired: { label: 'Expirée', color: 'bg-orange-100 text-orange-800' },
    revoked: { label: 'Révoquée', color: 'bg-rose-100 text-rose-800' },
    used: { label: 'Utilisée', color: 'bg-slate-100 text-slate-700' },
};

const REVIEW_PUB_STATUS = {
    draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
    verified_only: { label: 'En modération', color: 'bg-amber-100 text-amber-800' },
    published: { label: 'Publié', color: 'bg-emerald-100 text-emerald-800' },
    hidden: { label: 'Masqué', color: 'bg-slate-200 text-slate-700' },
    archived: { label: 'Archivé', color: 'bg-slate-100 text-slate-500' },
};

const DIMENSIONS_LABELS = {
    understands_project: 'Compréhension', explanations_clarity: 'Clarté',
    follow_up_quality: 'Suivi', regular_updates: 'Régularité',
    availability: 'Disponibilité', responsiveness: 'Réactivité',
    trust_feeling: 'Confiance', useful_advice: 'Conseils',
};

function StarsRow({ values }) {
    return (
        <div className="flex flex-wrap gap-2 mt-2">
            {Object.entries(values || {}).map(([k, v]) => (
                <span key={k} className="text-xs text-slate-600 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                    {DIMENSIONS_LABELS[k] || k}
                    <span className="ml-1 text-[#681793] font-medium">{v}/5</span>
                </span>
            ))}
        </div>
    );
}

function InvitationsList({ items, onRevoke }) {
    if (!items?.length) {
        return <p className="text-sm text-slate-500 italic">Aucune invitation envoyée pour le moment.</p>;
    }
    return (
        <ul className="space-y-2" data-testid="invitations-list">
            {items.map(inv => {
                const st = STATUS_BADGE[inv.status] || STATUS_BADGE.created;
                const canRevoke = !['submitted', 'used', 'revoked', 'expired'].includes(inv.status);
                return (
                    <li key={inv.id} data-testid={`invitation-${inv.id}`}
                        className="bg-white rounded-xl border border-slate-200 p-4 flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                                <Badge className={`${st.color} border text-xs`}>{st.label}</Badge>
                                {inv.expires_at && (
                                    <span className="text-xs text-slate-400">
                                        expire le {new Date(inv.expires_at).toLocaleDateString('fr-FR')}
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-500">
                                Envoyée le {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString('fr-FR') : '—'}
                                {inv.opened_at && ` · Ouverte le ${new Date(inv.opened_at).toLocaleDateString('fr-FR')}`}
                                {inv.submitted_at && ` · Soumise le ${new Date(inv.submitted_at).toLocaleDateString('fr-FR')}`}
                            </p>
                        </div>
                        {canRevoke && (
                            <Button size="sm" variant="ghost" onClick={() => onRevoke(inv.id)}
                                    data-testid={`btn-revoke-${inv.id}`}>
                                <Ban className="w-4 h-4 mr-1" /> Révoquer
                            </Button>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

function ReviewCard({ review, onReply }) {
    const st = REVIEW_PUB_STATUS[review.publication_status] || REVIEW_PUB_STATUS.draft;
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyText, setReplyText] = useState(review.professional_response || '');

    const submit = async () => {
        if (replyText.trim().length < 10) {
            toast.error('Votre réponse doit faire au moins 10 caractères.');
            return;
        }
        const ok = await onReply(review.id, replyText);
        if (ok) {
            setReplyOpen(false);
        }
        // si échec : on conserve le texte saisi et le formulaire ouvert
    };

    return (
        <div data-testid={`review-card-${review.id}`}
             className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Badge className={`${st.color} border text-xs`}>{st.label}</Badge>
                    {review.would_recommend === true && (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Recommande</Badge>
                    )}
                    {review.verification_status === 'verified' && (
                        <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Vérifié Homly</Badge>
                    )}
                </div>
                <span className="text-xs text-slate-400">
                    {review.submitted_at && new Date(review.submitted_at).toLocaleDateString('fr-FR')}
                </span>
            </div>
            <div>
                <p className="text-sm font-semibold text-slate-900">{review.public_display_name}</p>
                {review.free_comment && (
                    <p className="text-sm text-slate-700 mt-1">« {review.free_comment} »</p>
                )}
                <StarsRow values={review.dimensions} />
            </div>

            {review.professional_response && !replyOpen && (
                <div className="bg-slate-50 rounded-lg p-3 border-l-4 border-[#681793]">
                    <p className="text-xs text-[#681793] font-medium mb-1">Votre réponse</p>
                    <p className="text-sm text-slate-700">{review.professional_response}</p>
                </div>
            )}

            {review.publication_status === 'published' && (
                <>
                    {!replyOpen ? (
                        <Button size="sm" variant="outline" onClick={() => setReplyOpen(true)}
                                data-testid={`btn-reply-${review.id}`}>
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {review.professional_response ? 'Modifier ma réponse' : 'Répondre'}
                        </Button>
                    ) : (
                        <div className="space-y-2">
                            <textarea
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                maxLength={2000}
                                className="w-full p-2 border border-slate-200 rounded-lg text-sm"
                                rows={3}
                                placeholder="Votre réponse publique…"
                                data-testid={`textarea-reply-${review.id}`}
                            />
                            <div className="flex gap-2">
                                <Button size="sm" onClick={submit}
                                        className="bg-[#681793] hover:bg-[#7d2aac]"
                                        data-testid={`btn-submit-reply-${review.id}`}>
                                    Publier ma réponse
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setReplyOpen(false)}>
                                    Annuler
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function EligibleMandatesList({ items, onInvite, sending }) {
    if (!items?.length) {
        return <p className="text-sm text-slate-500 italic">Aucun mandat éligible pour le moment.</p>;
    }
    return (
        <ul className="space-y-2" data-testid="eligible-mandates-list">
            {items.map(m => (
                <li key={m.mandate_id} data-testid={`mandate-${m.mandate_id}`}
                    className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <p className="text-sm text-slate-900">
                            <span className="font-medium">Mandat</span>
                            <span className="text-slate-500 ml-2">{m.status}</span>
                            {m.city && <span className="text-slate-500 ml-2">· {m.city}</span>}
                        </p>
                        <span className="text-xs text-slate-400">
                            {m.invitations_sent > 0 && `${m.invitations_sent} invitation(s) envoyée(s)`}
                        </span>
                    </div>
                    <div className="space-y-1">
                        {m.sellers.map((s, i) => (
                            <div key={i} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-slate-700">
                                    {s.first_name} {s.last_name_initial && `${s.last_name_initial}.`}
                                </span>
                                <Button size="sm" variant="outline"
                                        onClick={() => onInvite(m.mandate_id, s.email)}
                                        disabled={sending}
                                        data-testid={`btn-invite-${m.mandate_id}-${i}`}>
                                    <Send className="w-4 h-4 mr-1" /> Inviter à donner son avis
                                </Button>
                            </div>
                        ))}
                    </div>
                </li>
            ))}
        </ul>
    );
}

export default function ReseauSellerReviewsPage() {
    const [mandates, setMandates] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [tab, setTab] = useState('mandates');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [m, i, r] = await Promise.all([
                api.get('/seller-reviews/eligible-mandates'),
                api.get('/seller-reviews/invitations'),
                api.get('/seller-reviews/mine'),
            ]);
            setMandates(m.data.items || []);
            setInvitations(i.data.items || []);
            setReviews(r.data.items || []);
        } catch (e) {
            toast.error('Impossible de charger les avis vendeurs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runAction = useCockpitAction();

    const handleInvite = async (mandateId, email) => {
        if (sending) return;
        setSending(true);
        try {
            const r = await api.post('/seller-reviews/invitations', {
                mandate_id: mandateId, seller_email: email,
            });
            if (r.data.already_sent) {
                toast.info('Une invitation est déjà en cours pour ce vendeur.');
            } else if (r.data.sent) {
                toast.success('Invitation envoyée');
            } else {
                toast.warning('Invitation créée — e-mail non envoyé (vérifiez la configuration)');
            }
            load();
        } catch (e) {
            const status = e?.response?.status;
            if (status && status >= 500) {
                toast.error("Erreur serveur. L'invitation n'a pas été envoyée. Réessayez.");
            } else if (status === 429) {
                toast.error('Trop de requêtes. Patientez quelques secondes.');
            } else {
                toast.error(e?.response?.data?.detail || "Invitation non envoyée.");
            }
        } finally {
            setSending(false);
        }
    };

    const handleRevoke = async (invId) => {
        if (!window.confirm('Révoquer cette invitation ?')) return;
        let snap = null;
        await runAction({
            key: `inv-revoke-${invId}`,
            optimistic: () => {
                snap = invitations;
                setInvitations(prev => prev.map(i => i.id === invId ? { ...i, status: 'revoked' } : i));
            },
            rollback:   () => { if (snap) setInvitations(snap); },
            call:       () => api.post(`/seller-reviews/invitations/${invId}/revoke`, { reason: 'manual_revoke' }),
            onSuccess:  () => { toast.success('Invitation révoquée. Le vendeur ne pourra plus l\'utiliser.'); load(); },
            errorLabel: "La révocation n'a pas été enregistrée.",
        });
    };

    /**
     * handleReply renvoie true/false pour permettre à ReviewCard de conserver
     * le texte saisi en cas d'échec.
     */
    const handleReply = async (reviewId, text) => {
        let snap = null;
        return await runAction({
            key: `review-reply-${reviewId}`,
            optimistic: () => {
                snap = reviews;
                setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, professional_response: text } : r));
            },
            rollback:   () => { if (snap) setReviews(snap); },
            call:       () => api.post(`/seller-reviews/${reviewId}/reply`, { text }),
            onSuccess:  () => { toast.success('Votre réponse est maintenant visible sous l\'avis sur votre profil public.'); load(); },
            errorLabel: "Votre réponse n'a pas été enregistrée.",
        });
    };

    const tabs = [
        { key: 'mandates', label: 'Mandats éligibles', count: mandates.length },
        { key: 'invitations', label: 'Invitations', count: invitations.length },
        { key: 'reviews', label: 'Avis reçus', count: reviews.length },
    ];

    return (
        <ReseauLayout headerTitle="Avis vendeurs vérifiés" headerSubtitle="Demandez à vos vendeurs un retour vérifié sur votre accompagnement. Chaque avis est multidimensionnel — pas de note unique. Le vendeur garde le contrôle de son identité publique.">
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-5" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                <ReviewSuggestionsCard onSent={load} />
                <div className="reseau-card p-2">
                    <div className="flex gap-1 overflow-x-auto" role="tablist">
                        {tabs.map(t => (
                            <button key={t.key}
                                    role="tab"
                                    aria-selected={tab === t.key}
                                    onClick={() => setTab(t.key)}
                                    data-testid={`tab-${t.key}`}
                                    className="px-4 py-2 rounded-lg text-sm whitespace-nowrap font-medium transition-colors"
                                    style={tab === t.key ? {
                                        background: 'var(--reseau-violet)', color: '#fff'
                                    } : {
                                        color: 'var(--reseau-ink-soft)'
                                    }}>
                                {t.label} ({t.count})
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--reseau-violet)' }} />
                    </div>
                ) : (
                    <div>
                        {tab === 'mandates' && (
                            <EligibleMandatesList items={mandates} onInvite={handleInvite} sending={sending} />
                        )}
                        {tab === 'invitations' && (
                            <InvitationsList items={invitations} onRevoke={handleRevoke} />
                        )}
                        {tab === 'reviews' && (
                            reviews.length === 0 ? (
                                <div className="reseau-empty" data-testid="empty-reviews">
                                    <Inbox className="w-10 h-10 mx-auto mb-3" style={{ color: 'var(--reseau-ink-faint)' }} />
                                    <h3>Les avis vérifiés aident les vendeurs à vous choisir</h3>
                                    <p>
                                        Ils racontent <strong>votre manière d&apos;accompagner</strong>, pas seulement votre performance. Invitez un vendeur que vous avez accompagné récemment.
                                    </p>
                                    <button
                                        onClick={() => setTab('eligible')}
                                        className="reseau-btn reseau-btn-primary mt-4"
                                        data-testid="empty-reviews-invite-cta">
                                        Inviter un vendeur récent
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {reviews.map(r => (
                                        <ReviewCard key={r.id} review={r} onReply={handleReply} />
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                )}
            </main>
        </ReseauLayout>
    );
}
